import usePrivateAxios from "@/common/api/usePrivateAxios"
import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper"
import GoBackButton from "@/common/components/GoBackButton"
import MaskedTextField from "@/common/components/MaskedTextField"
import SelectController from "@/common/components/SelectController"
import TextField from "@/common/components/TextField"
import { useEquipmentCategories } from "@/common/hooks/queries/useEquipmentCategories"
import { useEquipmentTiers } from "@/common/hooks/queries/useEquipmentTiers"
import { useProducts } from "@/common/hooks/queries/useProducts"
import useDebounce from "@/common/hooks/useDebounce"
import useUserPermissions from "@/common/hooks/useUserPermissions"
import { Mapper } from "@/common/services/mapper"
import { EquipmentTierData } from "@/common/types/equipmentTier"
import { EquipmentCategoryData } from "@/features/equipmentModels/types/equipmentCategories"
import { ProductData } from "@/features/products/types/product"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ChevronsRight } from "lucide-react"
import { useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { Link, useNavigate, useParams } from "react-router-dom"
import useEquipmentModel from "../../hooks/useEquipmentModel"
import { EquipmentModelValidationSchema, createSchema } from "../../schemas/create.schema"

const Update = () => {
  const { id } = useParams();
  const navigate = useNavigate()
  const privateAxios = usePrivateAxios();
  const queryClient = useQueryClient();

  const [serverError, setServerError] = useState('');
  const [productSearch, setProductSearch] = useDebounce('');
  const [equipmentCategorySearch, setEquipmentCategorySearch] = useDebounce('');
  const [searchEquipmentTier, setSearchEquipmentTier] = useDebounce('');

  const { data: equipmentTierOptions } = useEquipmentTiers({
    filters: { search: searchEquipmentTier },
    mapper: Mapper.mapToOptions<EquipmentTierData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  });

  const { mutate: updateEquipmentModel, isPending: updateEquipmentModelIsPending } = useMutation({
    mutationKey: ['updateEquipmentModel', id],
    mutationFn: async (form: EquipmentModelValidationSchema) => {
      const equipment_model = {
        ...form,
        category_id: form.category_id?.value,
        equipment_tier_id: form.equipment_tier?.value || null,
        applicable_product_ids: form.applicable_product_ids?.map((product) => product.value) || []
      };

      delete equipment_model.equipment_tier;

      return privateAxios.put(`/equipment_models/${id}`, {
        equipment_model
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipmentModels'] });
      queryClient.invalidateQueries({ queryKey: ['equipmentModel', id] });
      navigate(`/equipment-models/${id}`);
    },
    onError: () => {
      setServerError('generic');
    }
  });

  const {
    data: initialEquipment,
    isLoading
  } = useEquipmentModel(id || '')

  const { data: equipmentCategoryOptions } = useEquipmentCategories({
    filters: { search: equipmentCategorySearch },
    mapper: Mapper.mapToOptions<EquipmentCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { data: productOptions } = useProducts({
    filters: { search: productSearch },
    mapper: Mapper.mapToOptions<ProductData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasUpdateEquipmentModelPermission = !isUserPermissionsPending ? hasUserPermission('Admin::EquipmentModels::Update') : true

  const {
    register,
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<EquipmentModelValidationSchema>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      applicable_product_ids: initialEquipment?.applicable_products.map((product) => ({ label: product.display_name, value: product.id })),
      brand: initialEquipment?.brand,
      category_id: equipmentCategoryOptions?.find((category) => initialEquipment?.category.id === category.value),
      display_name: initialEquipment?.display_name,
      year: `${initialEquipment?.year}`,
      equipment_tier: initialEquipment?.equipment_tier ? {
        label: initialEquipment.equipment_tier.display_name,
        value: initialEquipment.equipment_tier.id
      } : null,
    }
  })

  const onSubmit: SubmitHandler<EquipmentModelValidationSchema> = (data) => updateEquipmentModel(data);

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasUpdateEquipmentModelPermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback={`/equipment-models/${id}`} />
            <p className="text-white-400 font-normal">Modelos de equipamento</p>
            <ChevronsRight color='#ffffffb3' />
            <h2>{initialEquipment?.display_name}</h2>
          </div>
          <div className="flex gap-4">
            <Link to={`/equipment-models/${id}`}><Button variant='outline-error'>Cancelar</Button></Link>
            <Button
              data-testid="update_equipment_models"
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              disabled={updateEquipmentModelIsPending}
              isLoading={updateEquipmentModelIsPending}
            >
              Confirmar
            </Button>
          </div>
        </div>
        {
          (isLoading || !equipmentCategoryOptions || !productOptions || isUserPermissionsPending)
            ? (
              <Box>Carregando...</Box>
            )
            : (
              <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
                {serverError == 'generic' && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>}
                <p className="text-lg font-bold">{initialEquipment?.display_name}</p>
                <div className="flex gap-2">
                  <SelectController
                    control={control}
                    name="category_id"
                    label="Categoria"
                    className="w-1/2"
                    errorMessage={errors?.category_id?.message}
                    defaultValue={equipmentCategoryOptions.find((category) => initialEquipment?.category.id === category.value)}
                    dataTestid="category_id_selector"
                    onInputChange={setEquipmentCategorySearch}
                    options={equipmentCategoryOptions}
                    isDisabled={updateEquipmentModelIsPending}
                  />
                  <MaskedTextField
                    label="Ano"
                    data-testid="year_input"
                    type="number"
                    mask='0000'
                    className="w-1/2"
                    defaultValue={initialEquipment?.year}
                    errorMessage={errors?.year?.message || undefined}
                    {...register('year')}
                    disabled={updateEquipmentModelIsPending}
                  />
                </div>
                <div className="flex gap-2">
                  <TextField
                    label="Marca"
                    data-testid="brand_input"
                    className="w-1/2"
                    defaultValue={initialEquipment?.brand}
                    errorMessage={errors?.brand?.message || undefined}
                    {...register('brand')}
                    disabled={updateEquipmentModelIsPending}
                  />
                  <TextField
                    label="Modelo"
                    className="w-1/2"
                    data-testid="display_name_input"
                    defaultValue={initialEquipment?.display_name}
                    errorMessage={errors?.display_name?.message || undefined}
                    {...register('display_name')}
                    disabled={updateEquipmentModelIsPending}
                  />
                </div>

                <SelectController
                  control={control}
                  name="equipment_tier"
                  label="Porte"
                  options={equipmentTierOptions || []}
                  onInputChange={setSearchEquipmentTier}
                  isClearable
                  errorMessage={errors?.equipment_tier?.message}
                  isDisabled={updateEquipmentModelIsPending}
                />

                <SelectController
                  control={control}
                  name="applicable_product_ids"
                  label="Produtos e serviços aplicáveis"
                  isMulti
                  dataTestid="applicable_product_ids_selector"
                  defaultValue={initialEquipment?.applicable_products.map((product) => ({ label: product.display_name, value: product.id }))}
                  onInputChange={(e) => setProductSearch(e)}
                  options={productOptions}
                  isDisabled={updateEquipmentModelIsPending}
                />
              </Box>
            )
        }
      </form>
    </ErrorMessageWrapper>
  )
}

export default Update
